import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import LayoutHeader          from './components/LayoutHeader';
import LayoutSidebar         from './components/LayoutSidebar';
import StudentsPage          from './components/StudentsPage';
import POSComponent          from './components/POSComponent';
import SalesPage             from './components/SalesPage';
import ProductManagementForm from './components/ProductManagementForm';
import FinancesPanel         from './components/FinancesPanel';
import CalendarComponent     from './components/CalendarComponent';
import TeacherManagementForm from './components/TeacherManagementForm';
import UserManagement        from './components/Auth/UserManagement';
import LoginForm             from './components/Auth/LoginForm';
import DashboardPanel        from './components/DashboardPanel';
import AttendancePanel       from './components/AttendancePanel';
import PaymentsPanel         from './components/PaymentsPanel';
import SharedCard            from './components/SharedCard';
import FestivalesPanel       from './components/Festivales/FestivalesPanel'; // FIX: import limpio, sin JSX suelto debajo

// ─── Guard de ruta ────────────────────────────────────────────────────────────
const ProtectedRoute = ({ currentUser, pageKey, children }) => {
  const isSuperAdmin  = currentUser?.username?.toLowerCase() === 'admin';
  if (isSuperAdmin) return children;

  const allowedPages = currentUser?.pages || [];
  if (allowedPages.includes('*'))      return children;
  if (allowedPages.includes(pageKey))  return children;

  return <Navigate to="/dashboard" replace />;
};

// ─── Layout principal ─────────────────────────────────────────────────────────
const ProtectedLayout = ({ currentUser, setCurrentUser }) => {
  const [isLeftSidebarOpen,  setIsLeftSidebarOpen]  = useState(false);
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(false);

  const toggleSidebar = side => {
    if (side === 'left') {
      setIsLeftSidebarOpen(prev => !prev);
      setIsRightSidebarOpen(false);
    } else {
      setIsRightSidebarOpen(prev => !prev);
      setIsLeftSidebarOpen(false);
    }
  };

  const isSuperAdmin = currentUser?.username?.toLowerCase() === 'admin';

  return (
    <div className="min-h-screen bg-white text-black">
      <LayoutHeader
        toggleSidebar={toggleSidebar}
        currentUser={currentUser}
        setCurrentUser={setCurrentUser}
      />
      <LayoutSidebar isOpen={isLeftSidebarOpen}  toggleSidebar={toggleSidebar} side="left"  currentUser={currentUser} />
      <LayoutSidebar isOpen={isRightSidebarOpen} toggleSidebar={toggleSidebar} side="right" currentUser={currentUser} />

      <main className="pt-20 pb-8 px-4 md:px-8 lg:px-16">
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          <Route path="/dashboard" element={<DashboardPanel currentUser={currentUser} />} />

          <Route path="/estudiantes" element={
            <ProtectedRoute currentUser={currentUser} pageKey="StudentsList">
              <StudentsPage currentUser={currentUser} />
            </ProtectedRoute>
          } />
          <Route path="/registro" element={
            <ProtectedRoute currentUser={currentUser} pageKey="StudentsList">
              <Navigate to="/estudiantes" replace />
            </ProtectedRoute>
          } />

          <Route path="/pos" element={
            <ProtectedRoute currentUser={currentUser} pageKey="POS">
              <POSComponent currentUser={currentUser} />
            </ProtectedRoute>
          } />

          <Route path="/ventas" element={
            <ProtectedRoute currentUser={currentUser} pageKey="SalesPage">
              <SalesPage currentUser={currentUser} />
            </ProtectedRoute>
          } />

          <Route path="/productos" element={
            <ProtectedRoute currentUser={currentUser} pageKey="ProductManagement">
              <ProductManagementForm />
            </ProtectedRoute>
          } />

          <Route path="/finanzas" element={
            <ProtectedRoute currentUser={currentUser} pageKey="FinancesPanel">
              <FinancesPanel />
            </ProtectedRoute>
          } />

          <Route path="/calendario" element={
            <ProtectedRoute currentUser={currentUser} pageKey="Calendar">
              <CalendarComponent />
            </ProtectedRoute>
          } />

          <Route path="/maestros" element={
            <ProtectedRoute currentUser={currentUser} pageKey="TeacherManagement">
              <TeacherManagementForm />
            </ProtectedRoute>
          } />

          <Route path="/asistencia" element={
            <ProtectedRoute currentUser={currentUser} pageKey="AttendancePanel">
              <AttendancePanel />
            </ProtectedRoute>
          } />

          <Route path="/pagos" element={
            <ProtectedRoute currentUser={currentUser} pageKey="PaymentsPanel">
              <PaymentsPanel />
            </ProtectedRoute>
          } />

          {/* FIX: ruta de Festivales agregada aquí dentro del componente, no suelta en el módulo */}
          <Route path="/festivales" element={
            <ProtectedRoute currentUser={currentUser} pageKey="FestivalesPanel">
              <FestivalesPanel currentUser={currentUser} />
            </ProtectedRoute>
          } />

          <Route path="/usuarios" element={
            isSuperAdmin ? (
              <UserManagement currentUser={currentUser} />
            ) : (
              <SharedCard title="Gestión de Usuarios">
                <p className="p-4 text-center text-red-600">No autorizado</p>
              </SharedCard>
            )
          } />

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
};

// ─── App raíz ─────────────────────────────────────────────────────────────────
const App = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    fetch('/api/session.php', { credentials: 'include' })
      .then(res => res.ok ? res.json() : { loggedIn: false })
      .then(data => {
        if (data.loggedIn) {
          setCurrentUser(data.user);
          checkBirthdays();
        }
      })
      .catch(() => {})
      .finally(() => setAuthChecked(true));
  }, []);

  const checkBirthdays = async () => {
    try {
      const res = await fetch('/api/check_birthdays.php', { credentials: 'include' });
      const ct  = res.headers.get('content-type') || '';
      if (!res.ok || !ct.includes('application/json')) return;
      const data = await res.json();
      if (data.success && data.count > 0) {
        const names = data.students.map(s => s.full_name).join(', ');
        alert(`🎉 Esta semana cumplen años: ${names}`);
      }
    } catch (err) {
      console.error('Error al verificar cumpleaños:', err);
    }
  };

  const handleLoginSuccess = async userData => {
    setCurrentUser(userData);
    await checkBirthdays();
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-gray-400 text-sm">Cargando...</p>
      </div>
    );
  }

  return (
    <BrowserRouter>
      {currentUser ? (
        <ProtectedLayout currentUser={currentUser} setCurrentUser={setCurrentUser} />
      ) : (
        <LoginForm onLoginSuccess={handleLoginSuccess} />
      )}
    </BrowserRouter>
  );
};

export default App;